import TaggedText from "pixi-tagged-text";
import { Align, FontStyle, ImageSource, ImageSourceMap, ParagraphToken, TaggedTextOptions, TextStyleExtended } from "pixi-tagged-text/dist/types";
import { Renderer, TextMetrics, TextStyle, Texture } from "pixi.js";
import { Orientation } from "../canvas/orientation";
import { DualPosition } from "./dual-position";
import { RenderOrientation } from "./render-orientation";
import { TextPosition } from "./text-position";

export class HTMLText extends TaggedText {
    public static minFontSize: number = 8;

    public static setTagStyling(tag: string, style: TextStyleExtended): void {
        this.styling[tag] = style;
    }

    public static getTextStyling(tag: string): TextStyleExtended | undefined {
        return this.styling[tag];
    }

    public static setImage(tag: string, image: ImageSource): void {
        this.imgMap[tag] = image;
    }

    protected static styling: { [key: string]: TextStyleExtended } = {};
    protected static imgMap: ImageSourceMap = {};

    public landscape: TextPosition = new TextPosition();
    public portrait: TextPosition = new TextPosition();

    private lastText: string;

    constructor(text?: string, style?: TextStyle, options?: TaggedTextOptions, texture?: Texture) {
        super(text, { default: {
            ...style,
            align: style.align as Align,
            valign: "middle",
            fontStyle: style.fontStyle as FontStyle
        }, ...HTMLText.styling }, { ...options, imgMap: HTMLText.imgMap }, texture);
    }

    public updateTransform() {
        const position = this.getOrientationPosition();

        super.x = position.x;
        super.y = position.y + ((position.height - this.getTextDimensions().height) / 2);

        super.updateTransform();

        this.forceTextRefresh();
    }

    public _render(renderer: Renderer) {
        if (this.getOrientationPosition().unavailable) {
            return;
        }

        return super._render(renderer);
    }

    public getPosition(orientation: Orientation) {
        return orientation === Orientation.PORTRAIT ? this.portrait : this.landscape;
    }

    public setDualPosition(dualPosition: DualPosition) {
        this.landscape.x = dualPosition.landscape.x;
        this.landscape.y = dualPosition.landscape.y;
        this.landscape.width = dualPosition.landscape.width;
        this.landscape.height = dualPosition.landscape.height;
        this.portrait.x = dualPosition.portrait.x;
        this.portrait.y = dualPosition.portrait.y;
        this.portrait.width = dualPosition.portrait.width;
        this.portrait.height = dualPosition.portrait.height;
    }

    public update(skipDraw?: boolean): ParagraphToken {

        // this.iOSFix(); // Removed as of PIXI 5.3.2 - Left commented out for now in case of false positive

        if (this.text !== this.lastText) {
            this.lastText = this.text;
            this.autoResize();
        }

        return super.update(skipDraw);
    }

    /**
     * This only exists as a way to force `updateText` to call `autoResize`.
     *
     * Useful when resizing text position's width & height programatically.
     */
    public forceAutoResize() {
        this.autoResize();
        super.update(false);
    }

    protected forceTextRefresh() {
        const text = this.text;
        this.lastText = "";
        this.setText("", true);
        this.setText(text, false);
    }

    protected autoResize() {
        const position = this.getOrientationPosition();

        if (position) {
            // Size adjustment is only applied to "default". Other styles adjust automatically relative to the default.
            this.tagStyles["default"].wordWrapWidth = position.width;
            this.tagStyles["default"].fontSize = position.fontSize;

            if (this.tagStyles["default"].fontSize > HTMLText.minFontSize) {
                let textDimensions = this.getTextDimensions();

                while (textDimensions.width > position.width ||
                    textDimensions.height > position.height) {
                    let size = Number(this.tagStyles["default"].fontSize);
                    size--;
                    this.tagStyles["default"].fontSize = size;

                    if (this.tagStyles["default"].fontSize <= HTMLText.minFontSize) {
                        break;
                    }

                    textDimensions = this.getTextDimensions();
                }
            }

            this.update(false);
        }
    }

    protected getTextDimensions() {
        return TextMetrics.measureText(this.untaggedText, new TextStyle(this.tagStyles["default"]));
    }

    // @ts-ignore
    get x() {
        throw new Error("Do not change y manually, please use landscape.y or portrait.y");
    }

    set x(value: number) {
        throw new Error("Do not change x manually, please use landscape.x or portrait.x");
    }

    // @ts-ignore
    get y() {
        throw new Error("Do not change y manually, please use landscape.y or portrait.y");
    }

    set y(value: number) {
        throw new Error("Do not change y manually, please use landscape.y or portrait.y");
    }

    // @ts-ignore
    get width() {
        throw new Error("Do not read width manually, please use landscape.width or portrait.width");
    }

    set width(value: number) {
        throw new Error("Do not change width manually, please use landscape.width or portrait.width");
    }

    // @ts-ignore
    get height() {
        throw new Error("Do not read height manually, please use landscape.height or portrait.height");
    }

    set height(value: number) {
        throw new Error("Do not change height manually, please use landscape.height or portrait.height");
    }

    private getOrientationPosition() {
        return RenderOrientation.isLandscape() ? this.landscape : this.portrait;
    }
}
